import { Badge, Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  EDUCATION_CHECKLIST_LOCKED_ITEMS,
  EncounterType,
  GaWindow,
  PrenatalExamData,
} from '@hb/shared'
import { collection, CollectionReference } from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../../../backend/db/db'
import { Expandable } from '../../../components/Expandable'
import { useQuery } from '../../../hooks/backend/useQuery'
import { useAssessmentExams } from '../../../hooks/backend/user/useAssessmentExams'

interface ChecklistItemDoc {
  text: string
  gaWindow: GaWindow
  sortOrder: number
  createdOn: number
}

interface VisitEntry {
  date: string | undefined
  checked: boolean
  note: string | undefined
}

interface ItemSummary {
  itemId: string
  itemText: string
  source: 'locked' | 'global' | 'practice' | 'personal'
  gaWindow: GaWindow
  sortOrder: number
  visits: VisitEntry[]
}

const GA_WINDOW_LABELS: Record<GaWindow, string> = {
  'first-trimester': 'First Trimester',
  'second-trimester': 'Second Trimester',
  'third-trimester': 'Third Trimester',
  postpartum: 'Postpartum',
}

const GA_WINDOW_ORDER: GaWindow[] = [
  'first-trimester',
  'second-trimester',
  'third-trimester',
  'postpartum',
]

export const EducationSummaryPanel = ({ assessmentId }: { assessmentId: string }) => {
  const { dataArr: encounters } = useAssessmentExams(assessmentId, EncounterType.ANTEPARTUM)

  const lockedItemsRef = useMemo(
    () => collection(db, EDUCATION_CHECKLIST_LOCKED_ITEMS) as CollectionReference<ChecklistItemDoc>,
    [],
  )
  const { dataArr: lockedItems } = useQuery(lockedItemsRef)

  const summaryByWindow = useMemo(() => {
    if (!encounters) return null

    const sortOrderByItemId = new Map<string, number>()
    if (lockedItems) {
      for (const li of lockedItems) {
        sortOrderByItemId.set(li.id, li.sortOrder)
      }
    }

    const itemMap = new Map<string, ItemSummary>()
    for (const enc of encounters) {
      const cl = (enc.data as PrenatalExamData | undefined)?.patientEducationChecklist
      if (!cl) continue
      for (const item of cl.items) {
        const existing = itemMap.get(item.itemId)
        const visit: VisitEntry = {
          date: enc.date ?? undefined,
          checked: item.checked,
          note: item.note,
        }
        if (existing) {
          existing.visits.push(visit)
        } else {
          itemMap.set(item.itemId, {
            itemId: item.itemId,
            itemText: item.itemText,
            source: item.source,
            gaWindow: cl.gaWindow,
            sortOrder: sortOrderByItemId.get(item.itemId) ?? 9999,
            visits: [visit],
          })
        }
      }
    }

    if (itemMap.size === 0) return null

    const byWindow = new Map<GaWindow, ItemSummary[]>()
    for (const summary of itemMap.values()) {
      const list = byWindow.get(summary.gaWindow) ?? []
      list.push(summary)
      byWindow.set(summary.gaWindow, list)
    }

    for (const list of byWindow.values()) {
      list.sort((a, b) => {
        const aLocked = a.source === 'locked' ? 0 : 1
        const bLocked = b.source === 'locked' ? 0 : 1
        if (aLocked !== bLocked) return aLocked - bLocked
        return a.sortOrder - b.sortOrder
      })
    }

    return byWindow
  }, [encounters, lockedItems])

  const { itemCount, visitCount } = useMemo(() => {
    if (!summaryByWindow) return { itemCount: 0, visitCount: 0 }
    let items = 0
    const uniqueDates = new Set<string>()
    for (const list of summaryByWindow.values()) {
      items += list.length
      for (const s of list) {
        for (const v of s.visits) {
          if (v.date) uniqueDates.add(v.date)
        }
      }
    }
    return { itemCount: items, visitCount: uniqueDates.size }
  }, [summaryByWindow])

  // No checklist data exists yet across any encounter — hide the panel entirely
  if (encounters !== null && summaryByWindow === null) return null

  const header = encounters
    ? `Education Summary (${itemCount} item${itemCount !== 1 ? 's' : ''} across ${visitCount} visit${visitCount !== 1 ? 's' : ''})`
    : 'Education Summary'

  return (
    <Expandable
      header={header}
      headerProps={{ px: 3 }}
      initExpanded={false}
      borderTop="1px solid"
      borderColor="gray.100"
      _dark={{ borderColor: 'whiteAlpha.100' }}>
      {encounters === null ? (
        <Spinner size="sm" m={4} />
      ) : (
        <VStack align="stretch" spacing={3} p={4}>
          {GA_WINDOW_ORDER.filter(w => summaryByWindow?.has(w)).map(window => (
            <Box key={window}>
              <Text
                fontSize="xs"
                fontWeight={700}
                color="gray.500"
                letterSpacing="0.5px"
                mb={1}
                textTransform="uppercase">
                {GA_WINDOW_LABELS[window]}
              </Text>
              <VStack align="stretch" spacing={2}>
                {(summaryByWindow?.get(window) ?? []).map(item => (
                  <Box
                    key={item.itemId}
                    pl={2}
                    borderLeft="2px solid"
                    borderColor={item.source === 'locked' ? 'purple.300' : 'gray.200'}
                    _dark={{
                      borderColor: item.source === 'locked' ? 'purple.500' : 'whiteAlpha.200',
                    }}>
                    <HStack mb={0.5}>
                      {item.source === 'locked' && (
                        <Badge colorScheme="purple" fontSize="2xs">
                          Required
                        </Badge>
                      )}
                      <Text fontSize="sm" fontWeight={500}>
                        {item.itemText}
                      </Text>
                    </HStack>
                    <HStack flexWrap="wrap" spacing={1} mt={0.5}>
                      {item.visits.map((v, i) => (
                        <Box key={i}>
                          <HStack
                            spacing={1}
                            px={1.5}
                            py={0.5}
                            borderRadius={4}
                            bg={v.checked ? 'green.50' : 'gray.50'}
                            border="1px solid"
                            borderColor={v.checked ? 'green.200' : 'gray.200'}
                            _dark={{
                              bg: v.checked ? 'green.900' : 'gray.700',
                              borderColor: v.checked ? 'green.700' : 'whiteAlpha.300',
                            }}>
                            <Text
                              fontSize="xs"
                              color={v.checked ? 'green.700' : 'gray.400'}
                              _dark={{ color: v.checked ? 'green.300' : 'gray.500' }}>
                              {v.checked ? '✓' : '—'}
                            </Text>
                            <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
                              {v.date ?? 'Unknown date'}
                            </Text>
                          </HStack>
                          {v.note && (
                            <Text fontSize="xs" color="gray.500" fontStyle="italic" ml={1} mt={0.5}>
                              {v.note}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Expandable>
  )
}
